<template>
  <div class="flex flex-col gap-6 z-1000 w-full h-full">
    <loading-overlay v-if="loading" :value="loading" />
    <div v-else class="flex w-full items-start justify-center">
      <BaseTabs :tabs="tabs" :active-tab="activeTab" @change:tab="changeTab">
        <template #action>
          <div class="flex items-center gap-2">
            <el-dropdown
              v-if="uuid && availableDownloadLanguages.length"
              trigger="click"
              @command="handleDownloadCommand"
            >
              <Button variant="outline" size="icon">
                <svg-icon
                  name="huge-download"
                  class="w-4 h-4 text-foreground"
                />
              </Button>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item
                    v-for="item in availableDownloadLanguages"
                    :key="item.lang"
                    :command="item.lang"
                  >
                    {{ item.label }}
                  </el-dropdown-item>
                  <el-dropdown-item
                    v-if="availableDownloadLanguages.length > 1"
                    divided
                    command="all"
                  >
                    {{ $t("Скачать все языки") }}
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
            <el-dropdown
              v-if="uuid && availableDownloadLanguages.length"
              trigger="click"
              @command="handlePrintCommand"
            >
              <Button variant="outline" size="icon">
                <svg-icon name="huge-print" class="w-4 h-4 text-foreground" />
              </Button>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item
                    v-for="item in availableDownloadLanguages"
                    :key="item.lang"
                    :command="item.lang"
                  >
                    {{ item.label }}
                  </el-dropdown-item>
                  <el-dropdown-item
                    v-if="availableDownloadLanguages.length > 1"
                    divided
                    command="all"
                  >
                    {{ $t("Печать всех языков") }}
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
            <div v-if="!hereditaryView" class="flex items-center gap-2">
              <el-dropdown
                trigger="click"
                @command="handleStatusChange"
                :disabled="statusChanging"
              >
                <Button
                  variant="outline"
                  class="gap-2"
                  :disabled="statusChanging"
                >
                  <StatusDot :code="form.status" />
                  <span>{{ getStatusLabel(form.status) }}</span>
                  <!--                  <svg-icon-->
                  <!--                    name="huge-arrow-down"-->
                  <!--                    class="w-3 h-3 text-muted-foreground"-->
                  <!--                  />-->
                  <el-icon class="ml-0.5"><arrow-down /></el-icon>
                </Button>
                <template #dropdown>
                  <el-dropdown-menu v-if="availableStatuses.length">
                    <el-dropdown-item
                      v-for="status in availableStatuses"
                      :key="status.value"
                      :command="status.value"
                      :disabled="status.value === form.status"
                    >
                      <div class="flex items-center gap-2">
                        <StatusDot :code="status.value" />
                        <span>{{ status.label }}</span>
                      </div>
                    </el-dropdown-item>
                  </el-dropdown-menu>
                  <el-dropdown-menu v-else>
                    <el-dropdown-item disabled>
                      <span class="text-muted-foreground">
                        {{ $t("Нет доступных действий") }}
                      </span>
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
              <el-tooltip :content="$t('Режим просмотра')" placement="top">
                <Button variant="outline" size="icon" @click="goToView">
                  <svg-icon name="huge-eye" class="w-4 h-4 text-foreground" />
                </Button>
              </el-tooltip>
              <Button
                v-if="form.status === StatusCode.PROJECT"
                variant="default"
                @click="submitForm"
                :disabled="loading"
                >{{ $t("Сохранить") }}</Button
              >
            </div>

            <div v-else class="flex items-center gap-3">
              <Button variant="outline" size="icon" @click="goToEdit">
                <svg-icon name="huge-edit" class="w-4 h-4 text-foreground" />
              </Button>
            </div>
          </div>
        </template>
      </BaseTabs>
    </div>

    <div
      ref="formContainerRef"
      class="relative flex-1 w-full h-full flex min-h-0"
    >
      <Stepper
        v-model:open="stepperOpen"
        v-model:expanded-key="expandedStepKey"
        :steps="steps"
        :done-count="doneCount"
        :total-steps="totalSteps"
        :progress="progress"
        @step-action="onStepperAction"
      />

      <component
        :is="currentTabComponent"
        :form="form"
        :template="template"
        :template-items="templateItems"
        :key="form.id"
        :testament-nd-id="testamentData?.id"
        :testament-nd-number="testamentData?.number"
        @insert:field="insertFieldCollectedAmount"
        @change:tab="changeTab"
        @request:sign="changeTab('sign')"
        @paid="refreshStatusOnly()"
        @select:template="handleTemplateSelect"
        @testament-data="(data) => (testamentData = data)"
        @access-changed="handleAccessChanged"
        @reload="getNotaryTransactionItem(uuid)"
        ref="activeForm"
        class="flex-1 w-full h-full"
      />
    </div>

    <StepNavBar
      :open="navBarVisible"
      storage-key="hereditary-case-bar"
      :show-stepper="!!uuid && !hereditaryView"
      v-model:stepper-open="stepperOpen"
      :done-count="doneCount"
      :total-steps="totalSteps"
      :progress="progress"
      :has-prev="hasPrevTab"
      :has-next="hasNextTab"
      @prev="goToPrevTab"
      @next="tryGoNext"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { toast } from "@/shared/store/alertStore";
import LoadingOverlay from "@/shared/ui/custom/components/LoadingOverlay.vue";
import BaseTabs from "@/shared/ui/custom/tabs/BaseTabs.vue";
import NotarialActionMain from "@/pages/admin/home/notarial_actions/tabs/NotarialActionMain.vue";
import InheritanceRedactor from "@/pages/admin/hereditary_cases/components/InheritanceRedactor.vue";
import NotarialActionSign from "@/pages/admin/home/notarial_actions/tabs/NotarialActionSign.vue";
import NotarialActionAttachments from "@/pages/admin/home/notarial_actions/tabs/NotarialActionAttachments.vue";
import { getTemplateItem, getTemplateGroupedList } from "@/entities/template";
import NotarialActionHistoryView from "@/pages/admin/home/notarial_actions/tabs/view/NotarialActionHistoryView.vue";
import NotarialActionCalculation from "@/pages/admin/home/notarial_actions/tabs/NotarialActionCalculation.vue";
import { PaymentTab } from "@/features/payments";
import Button from "@/shared/ui/button/Button.vue";
import StepNavBar from "@/shared/ui/step-nav-bar/StepNavBar.vue";
import { Stepper, useStepperState } from "@/shared/ui/stepper";
import { useUnfilledMentions } from "@/shared/lib/composables/useUnfilledMentions";
import { useTabFlow } from "@/shared/lib/composables/useTabFlow";
import {
  StatusCode,
  getStatusLabel,
  ensureStatusDictionary,
} from "@/shared/config/statusDictionary";
import StatusDot from "@/shared/ui/status-dot/StatusDot.vue";
import { useAvailableActions } from "@/features/notarial-actions/composables/useAvailableActions";
import { createStepRun, generatePdfFromTipTap } from "@/entities/transaction";
import {
  createInheritance,
  changeStatusInheritance,
} from "@/entities/inheritance/api";
import { useInheritanceItem } from "@/entities/inheritance/model/useInheritanceItem";
import { CATEGORY_CODES } from "@/shared/lib/constants";
import { useSessionKeepAlive } from "@/shared/auth/useSessionKeepAlive";

defineProps({
  references: {
    type: Array,
  },
});

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const uuid = computed(() => route.params.id);
const template_code = computed(() => route.query.code);

const { form, loading, fetchInheritance, updateInheritanceItem } =
  useInheritanceItem(uuid);

const activeTab = ref("redactor");
const activeForm = ref(null);
const template = ref(null);
const testamentData = ref(null);
const pendingFields = ref([]);
const templateItems = ref([]);
const hereditaryView = computed(() => route.name === "hereditary_case_view");
const accessGranted = ref(true);
const statusChanging = ref(false);

const formContainerRef = ref(null);

// Вырезаем шумовые атрибуты (isLoading у mention, signatureVersion у signature),
// которые меняются без правки пользователя и иначе давали бы ложный dirty.
function normalizeDoc(value) {
  if (Array.isArray(value)) return value.map(normalizeDoc);
  if (value && typeof value === "object") {
    const out = {};
    for (const key of Object.keys(value)) {
      if (key === "attrs" && value.attrs && typeof value.attrs === "object") {
        // eslint-disable-next-line no-unused-vars
        const { isLoading, signatureVersion, ...rest } = value.attrs;
        out.attrs = rest;
      } else {
        out[key] = normalizeDoc(value[key]);
      }
    }
    return out;
  }
  return value;
}

function serializeContent() {
  return JSON.stringify({
    content: normalizeDoc(form.content ?? null),
    content_kz: normalizeDoc(form.content_kz ?? null),
    content_en: normalizeDoc(form.content_en ?? null),
  });
}

const savedContentSnapshot = ref(null);

function captureContentBaseline() {
  savedContentSnapshot.value = serializeContent();
}

const contentSignature = computed(() => serializeContent());

const isFormDirty = computed(() => {
  if (savedContentSnapshot.value === null) return false;
  return contentSignature.value !== savedContentSnapshot.value;
});

const navBarVisible = computed(
  () =>
    !hereditaryView.value &&
    form.status === StatusCode.PROJECT &&
    !loading.value
);

const { unfilledMentionsCount } = useUnfilledMentions(() => [
  form.content,
  form.content_kz,
  form.content_en,
]);

useSessionKeepAlive(isFormDirty);

const stepperOpen = ref(false);
const expandedStepKey = ref(null);
const visitedTabs = ref({});

function markTabVisited(tab) {
  visitedTabs.value = { ...visitedTabs.value, [tab]: true };
}

function hasContent(value) {
  if (!value) return false;
  if (typeof value === "string") return value.length > 0;
  if (typeof value !== "object") return false;
  if (Array.isArray(value)) return value.length > 0;
  return Object.keys(value).length > 0;
}

const paramsStats = computed(() => {
  const fields = form?.template_fields || {};
  const entries = Object.values(fields);
  const total = entries.length;
  const filled = entries.filter(
    (f) => f && f.value != null && f.value !== ""
  ).length;
  return { total, filled };
});

const doneCreated = computed(() => Boolean(form?.uuid || form?.id));
const doneEditor = computed(
  () => hasContent(form?.content) || hasContent(form?.content_kz)
);
const doneParams = computed(() => {
  if (visitedTabs.value.main) return true;
  const s = paramsStats.value;
  if (s.total === 0) return true;
  return s.filled === s.total;
});
const doneTariff = computed(() => Boolean(visitedTabs.value.tariff));
const doneAttachments = computed(() => Boolean(visitedTabs.value.attachments));
const doneSign = computed(
  () =>
    form?.status === StatusCode.SIGNED || form?.status === StatusCode.COMPLETED
);

const stepDefinitions = computed(() => [
  {
    key: "created",
    index: 1,
    title: t("Дело создано"),
    description: t("Наследственное дело зарегистрировано в системе."),
    icon: "huge-add-pen",
    done: doneCreated.value,
    summary: doneCreated.value ? "№ " + (form?.id || form?.uuid) : "",
    details: {
      todo: [t("Шаблон применён"), t("Получен идентификатор записи")],
      doneData: doneCreated.value
        ? [{ label: t("ID"), value: String(form?.id || form?.uuid || "") }]
        : [],
      warning: null,
      actionLabel: null,
      targetTab: null,
    },
  },
  {
    key: "redactor",
    index: 2,
    title: t("Редактор"),
    description: t("Заполните текст документа."),
    icon: "huge-edit",
    done: doneEditor.value && unfilledMentionsCount.value === 0,
    summary:
      unfilledMentionsCount.value > 0
        ? t("Не заполнено полей: ") + unfilledMentionsCount.value
        : doneEditor.value
        ? t("Текст заполнен")
        : "",
    details: {
      todo: [
        t("Проверьте подставленные данные"),
        unfilledMentionsCount.value > 0
          ? t("Заполните незаполненные поля: ") + unfilledMentionsCount.value
          : t("Заполните пустые поля"),
        t("Переключите язык и сверьте обе версии"),
      ],
      doneData:
        doneEditor.value && unfilledMentionsCount.value === 0
          ? [{ label: t("Статус"), value: t("Текст заполнен") }]
          : [],
      warning:
        unfilledMentionsCount.value > 0
          ? t("Осталось заполнить полей: ") +
            unfilledMentionsCount.value +
            ". " +
            t("Заполните их, чтобы продолжить.")
          : null,
      actionLabel: t("Открыть редактор"),
      targetTab: "redactor",
    },
  },
  {
    key: "main",
    index: 3,
    title: t("Параметры"),
    description: t("Укажите параметры дела."),
    icon: "huge-text-config",
    done: doneParams.value,
    summary: doneParams.value
      ? t("Заполнено: ") +
        paramsStats.value.filled +
        "/" +
        paramsStats.value.total
      : "",
    details: {
      todo: [t("Заполните обязательные поля шаблона")],
      doneData: doneParams.value
        ? [
            {
              label: t("Поля"),
              value: paramsStats.value.filled + "/" + paramsStats.value.total,
            },
          ]
        : [],
      warning: null,
      actionLabel: t("Открыть параметры"),
      targetTab: "main",
    },
  },
  {
    key: "tariff",
    index: 4,
    title: t("Тарифы"),
    description: t("Выберите тариф и рассчитайте стоимость."),
    icon: "huge-tariff",
    done: doneTariff.value,
    summary: doneTariff.value ? t("Тариф выбран") : "",
    details: {
      todo: [t("Выберите подходящий тариф")],
      doneData: [],
      warning: null,
      actionLabel: t("Открыть тарифы"),
      targetTab: "tariff",
    },
  },
  {
    key: "attachments",
    index: 5,
    title: t("Вложения"),
    description: t("Прикрепите подтверждающие документы."),
    icon: "huge-file-attach",
    done: doneAttachments.value,
    summary: doneAttachments.value ? t("Проверено") : "",
    details: {
      todo: [t("Прикрепите PDF-документы")],
      doneData: [],
      warning: null,
      actionLabel: t("Открыть вложения"),
      targetTab: "attachments",
    },
  },
  {
    key: "sign",
    index: 6,
    title: t("Подписание"),
    description: t("Подпишите документ ЭЦП."),
    icon: "huge-editor-magic-wand",
    done: doneSign.value,
    summary: doneSign.value
      ? getStatusLabel(form?.status) || t("Подписан")
      : "",
    details: {
      todo: [t("Завершите все предыдущие шаги"), t("Нажмите «Подписать ЭЦП»")],
      doneData: doneSign.value
        ? [
            {
              label: t("Статус"),
              value: getStatusLabel(form?.status) || t("Подписан"),
            },
          ]
        : [],
      warning: t("После подписания изменения недоступны."),
      actionLabel: t("Открыть подписание"),
      targetTab: "sign",
    },
  },
]);

const { steps, doneCount, totalSteps, progress } = useStepperState({
  definitions: stepDefinitions,
  activeTab,
});

function onStepperAction(step) {
  if (step?.details?.targetTab) {
    changeTab(step.details.targetTab);
  }
}

const availableActionsSource = computed(() => ({
  transactionId: form.id,
}));

const {
  availableActions,
  currentStatus,
  refresh: refreshAvailableActions,
} = useAvailableActions(availableActionsSource);

async function refreshStatusOnly() {
  await refreshAvailableActions();
  if (currentStatus.value) form.status = currentStatus.value;
}

const availableStatuses = computed(() =>
  availableActions.value.map((a) => ({ value: a.code, label: a.label }))
);

const tabs = computed(() => {
  if (hereditaryView.value) {
    return {
      redactor: {
        label: t("Документ"),
        icon: "huge-eye",
      },
      attachments: { label: t("Вложения"), icon: "huge-file-attach" },
      payment: {
        label: t("Оплата"),
        icon: "huge-document-info",
      },
      history: {
        label: t("История"),
        icon: "huge-history",
      },
    };
  }

  if (form.status === StatusCode.CANCELLED) {
    return {
      redactor: {
        label: t("Информация"),
        icon: "huge-note",
      },
    };
  }

  const baseTabs = {
    redactor: {
      label: t("Редактор"),
      icon: "huge-note",
    },
    main: {
      label: t("Параметры"),
      icon: "huge-text-config",
    },
    tariff: {
      label: t("Тарифы"),
      icon: "huge-tariff",
    },
    payment: {
      label: t("Оплата"),
      icon: "huge-document-info",
    },
    attachments: { label: t("Вложения"), icon: "huge-file-attach" },
    sign: {
      label: t("Подписание"),
      icon: "huge-editor-magic-wand",
    },
  };

  return baseTabs;
});

const availableDownloadLanguages = computed(() => {
  const langLabels = { ru: "Русский", kz: "Қазақша", en: "English" };
  return getAvailableTemplatePayloads().map((p) => ({
    lang: p.lang,
    label: langLabels[p.lang] || p.lang,
  }));
});

const tabComponents = {
  main: NotarialActionMain,
  tariff: NotarialActionCalculation,
  payment: PaymentTab,
  redactor: InheritanceRedactor,
  attachments: NotarialActionAttachments,
  sign: NotarialActionSign,
  history: NotarialActionHistoryView,
};

const currentTabComponent = computed(
  () => tabComponents[activeTab.value] || null
);

watch(
  () => route.query.tab,
  async (tab) => {
    activeTab.value = tab || "redactor";
  },
  { immediate: true }
);

watch(
  () => route.name,
  (newName) => {
    if (uuid.value && uuid.value !== "new") {
      getNotaryTransactionItem(uuid.value);
    }

    if (newName === "hereditary_case_view" && activeTab.value === "sign") {
      activeTab.value = "redactor";
    } else if (
      newName !== "hereditary_case_view" &&
      activeTab.value === "history"
    ) {
      activeTab.value = "redactor";
    }
  }
);

async function fetchTemplateItem(create_notary = false) {
  loading.value = true;
  try {
    let data = await getTemplateItem(template_code.value || form.template_code);
    if (data?.success) {
      if (data.payload) {
        template.value = data.payload;
        if (template_code.value && create_notary) {
          await createNotaryTransaction();
        }
      }
    }
  } finally {
    setTimeout(() => (loading.value = false), 500);
  }
}

async function getNotaryTransactionItem(id) {
  const data = await fetchInheritance(id);
  if (data?.success && data.payload) {
    await fetchTemplateItem();
  }
  await nextTick();
  captureContentBaseline();
}

async function handleStatusChange(status) {
  if (status === form.status) return;
  statusChanging.value = true;
  try {
    const data = await changeStatusInheritance(uuid.value, { status });
    if (data?.success) {
      form.status = status;
      await refreshAvailableActions();
    }
  } catch (e) {
    toast.error(t("Не удалось изменить статус"));
  } finally {
    statusChanging.value = false;
  }
}

function goToEdit() {
  router.push({
    name: "hereditary_case_edit",
    params: { id: uuid.value },
    query: { tab: activeTab.value },
  });
}

function goToView() {
  router.push({
    name: "hereditary_case_view",
    params: { id: uuid.value },
    query: { tab: activeTab.value },
  });
}

async function changeTab(tab) {
  if (!(await flow.maybeAutosaveBeforeLeave(tab))) return;

  router.replace({
    query: {
      ...route.query,
      tab,
    },
  });

  activeTab.value = tab;
  markTabVisited(tab);

  if (tab === "redactor" && pendingFields.value.length > 0) {
    const unwatch = watch(
      activeForm,
      (newRef) => {
        if (newRef && typeof newRef.insertFieldsAsMentions === "function") {
          nextTick(() => {
            newRef.insertFieldsAsMentions([...pendingFields.value]);
            pendingFields.value = [];
          });
          unwatch();
        }
      },
      { immediate: true }
    );
  }
}

const flow = useTabFlow({
  tabs,
  activeTab,
  isFormDirty,
  serializeContent,
  savedContentSnapshot,
  canAutosave: () =>
    !hereditaryView.value && form?.status === StatusCode.PROJECT,
  buildPayload: () => {
    // eslint-disable-next-line no-unused-vars
    const { _repeatSectionTemplate, ...cleanPayload } = form;
    if (!cleanPayload.contract_amount) {
      cleanPayload.contract_amount = 0;
    }
    return cleanPayload;
  },
  update: (payload) => updateInheritanceItem(uuid.value, payload),
  changeTab,
  onBlockedNext: () => {
    if (activeTab.value === "redactor" && unfilledMentionsCount.value > 0) {
      stepperOpen.value = true;
      expandedStepKey.value = "redactor";
      return true;
    }
    return false;
  },
  onAutosaveError: () => toast.error(t("Не удалось сохранить изменения")),
});

const { hasPrevTab, hasNextTab, goToPrevTab, tryGoNext } = flow;

async function createNotaryTransaction() {
  let params = {
    ...form,
    content: template.value.content,
    content_kz: template.value.content_kz,
    content_en: template.value.content_en,
    languages: template.value?.languages,
    fields: template.value.fields,
    template_fields: template.value.template_fields,
  };
  let data = await createInheritance(template.value.code, params);
  if (data?.success) {
    if (data.payload?.id) {
      await doCreateStepRun(data.payload);
      router.push({
        name: "hereditary_case_edit",
        params: { id: data.payload.id },
      });
    }
  }
}

async function submitForm() {
  let payload = await preparePayload();
  if (!payload) {
    console.warn("Nothing to submit");
    return;
  }

  loading.value = true;
  try {
    await doUpdateNotarial(payload);
  } finally {
    loading.value = false;
  }
}

async function preparePayload() {
  const activeTabRef = activeForm.value;

  if (!activeTabRef) {
    console.warn("No active tab ref");
    return;
  }
  let payload = null;

  if (typeof activeTabRef.submit === "function") {
    payload = await activeTabRef.submit();
    if (!payload) return null;
  } else {
    payload = form;
  }

  if (!payload.contract_amount) {
    payload.contract_amount = 0;
  }
  return payload;
}

async function doUpdateNotarial(payload, isNotifyDisabled) {
  let data = await updateInheritanceItem(uuid.value, payload);

  if (isNotifyDisabled) {
    return 0;
  }
  if (data?.success) {
    await fetchInheritance(uuid.value);
    await nextTick();
    captureContentBaseline();
  }
}

async function doCreateStepRun(notary) {
  let params = {
    notary_transaction_id: notary.id,
    notary_transaction_uuid: notary.uuid,
    template_uuid: template.value?.uuid || form?.template_uuid,
  };
  let response = await createStepRun(params);
  if (response?.success) {
    // Step run created successfully
  }
}

function handleDownloadCommand(command) {
  const payloads = getAvailableTemplatePayloads();
  if (command !== "all") {
    const hasContent = payloads.some((p) => p.lang === command);
    if (!hasContent) {
      toast.error(t("Контент для данного языка пустой"));
      return;
    }
  }
  if (!payloads.length) {
    toast.error(t("Нет данных для скачивания"));
    return;
  }
  if (command === "all") {
    downloadDocuments();
  } else {
    downloadDocuments(command);
  }
}

async function downloadDocuments(lang) {
  const language = lang || "all";

  const response = await generatePdfFromTipTap(uuid.value, { lang: language });
  if (!response?.data) {
    toast.error(t("Не удалось сформировать PDF"));
    return;
  }

  const blob =
    response.data instanceof Blob
      ? response.data
      : new Blob([response.data], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = createFileName(language);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function handlePrintCommand(command) {
  const payloads = getAvailableTemplatePayloads();
  if (command !== "all") {
    const hasContent = payloads.some((p) => p.lang === command);
    if (!hasContent) {
      toast.error(t("Контент для данного языка пустой"));
      return;
    }
  }
  if (!payloads.length) {
    toast.error(t("Нет данных для печати"));
    return;
  }
  if (command === "all") {
    printDocument();
  } else {
    printDocument(command);
  }
}

async function printDocument(lang) {
  const language = lang || "all";

  const response = await generatePdfFromTipTap(uuid.value, { lang: language });
  if (!response?.data) {
    toast.error(t("Не удалось сформировать PDF"));
    return;
  }

  const blob =
    response.data instanceof Blob
      ? response.data
      : new Blob([response.data], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);

  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.src = url;

  iframe.onload = () => {
    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();
  };

  document.body.appendChild(iframe);

  const cleanup = () => {
    window.URL.revokeObjectURL(url);
    iframe.remove();
    window.removeEventListener("focus", cleanup);
  };
  window.addEventListener("focus", cleanup, { once: true });
}

function getAvailableTemplatePayloads() {
  const candidates = [
    { lang: "ru", json: form?.content },
    { lang: "kz", json: form?.content_kz },
    { lang: "en", json: form?.content_en },
  ];

  return candidates
    .map((item) => {
      let data = item.json;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (error) {
          console.error("Invalid template JSON", error);
          data = null;
        }
      }
      return { lang: item.lang, data };
    })
    .filter((item) => item.data && item.data.type === "doc");
}

function createFileName(lang) {
  const base = form?.number || form?.uuid || form?.id || t("Документ");
  return `${base}_${lang}.pdf`;
}

function insertFieldCollectedAmount(field) {
  pendingFields.value.push(field);

  nextTick(() => {
    const activeFormRef = activeForm.value;

    if (
      activeTab.value === "redactor" &&
      activeFormRef &&
      typeof activeFormRef.insertFieldsAsMentions === "function"
    ) {
      activeFormRef.insertFieldsAsMentions([...pendingFields.value]);
      pendingFields.value = [];
    }
  });
}

async function loadTemplateItems() {
  try {
    const res = await getTemplateGroupedList({
      category_codes: CATEGORY_CODES.SVID_PRAVO_NASLED,
    });
    if (res?.success && Array.isArray(res.payload)) {
      templateItems.value = res.payload.flatMap((cat) =>
        (cat.items || []).map((item) => ({
          code: item.code,
          name: item.name || item.name_kz || item.code,
        }))
      );
    }
  } catch (e) {
    console.error(e);
  }
}

function handleAccessChanged(val) {
  accessGranted.value = val;
  if (!val) {
    activeTab.value = "redactor";
  }
}

async function handleTemplateSelect(code) {
  loading.value = true;
  try {
    const data = await getTemplateItem(code);
    if (data?.success && data.payload) {
      template.value = data.payload;
      form.template_code = code;
      form.content = data.payload.content;
      form.content_kz = data.payload.content_kz;
      form.content_en = data.payload.content_en;
      form.languages = data.payload.languages;
      form.fields = data.payload.fields;
      form.template_fields = data.payload.template_fields;
    }
  } finally {
    setTimeout(() => (loading.value = false), 500);
  }
}

// created logic
if (template_code.value) {
  fetchTemplateItem(true);
}
if (uuid.value && uuid.value !== "new") {
  getNotaryTransactionItem(uuid.value);
}
loadTemplateItems();
ensureStatusDictionary();
</script>

<style scoped lang="scss">
.button {
  @apply h-10 flex justify-center items-center p-4 gap-2 bg-card border;
  @apply border-border rounded outline-none;
  &:active {
    transform: scale(0.98);
  }
}

.menu {
  @apply w-[282px] flex flex-col p-2 gap-2 bg-card rounded-xl outline outline-1 outline-offset-[-1px];
}

.item {
  @apply flex items-center px-3 py-2 gap-3 rounded-lg cursor-pointer transition-colors;
  svg {
    @apply text-foreground;
    &.danger {
      @apply text-red-400;
    }
  }

  span {
    @apply text-base font-medium leading-6 text-foreground;
    &.danger {
      @apply text-red-400;
    }
  }
}
</style>
